/**
 * Optional PDF page rasterization (Faz 9.7, OCS PDFATTACH underlay path).
 *
 * pdf.js (`pdfjs-dist`) is an OPTIONAL runtime peer: npm installs are not
 * part of the Faz 9 scope, so the package specifier is resolved through a
 * variable dynamic import (never bundled, never type-resolved) and a
 * missing package degrades gracefully — PDFATTACH still places a scaled
 * underlay frame exactly like the DXF PDFUNDERLAY quad, with an honest
 * notify telling the user what installing pdfjs-dist adds. When present,
 * the chosen page renders at 2× into a PNG data URL with real page size.
 */

export interface PdfPageRaster {
    /** PNG data URL of the rendered page. */
    dataUrl: string;
    /** Page size in world meters (pt → m conversion applied). */
    widthMeters: number;
    heightMeters: number;
    pageCount: number;
}

const PT_TO_M = 0.0254 / 72;

interface PdfJsApi {
    getDocument: (src: { data: ArrayBuffer }) => { promise: Promise<PdfJsDoc> };
    GlobalWorkerOptions?: { workerSrc?: string };
}

interface PdfJsDoc {
    numPages: number;
    getPage: (n: number) => Promise<{
        getViewport: (opts: { scale: number }) => { width: number; height: number };
        render: (params: { canvasContext: CanvasRenderingContext2D; viewport: unknown }) => { promise: Promise<void> };
    }>;
}

/** Renders one page (0-based) of a PDF file; null when pdf.js is absent. */
export async function renderPdfPage(file: File, pageIndex0: number): Promise<PdfPageRaster | null> {
    const spec = 'pdfjs-dist';
    let pdfjs: PdfJsApi | null = null;
    try {
        const mod = (await import(/* @vite-ignore */ spec)) as unknown as {
            default?: PdfJsApi;
            GlobalWorkerOptions?: { workerSrc?: string };
        } & PdfJsApi;
        pdfjs = (mod.default ?? mod) as PdfJsApi;
        // Worker asset only resolves when the package is installed; pdf.js
        // falls back to its fake worker when the URL 404s at runtime.
        if (pdfjs && pdfjs.GlobalWorkerOptions && !pdfjs.GlobalWorkerOptions.workerSrc) {
            try {
                pdfjs.GlobalWorkerOptions.workerSrc = new URL(
                    'pdfjs-dist/build/pdf.worker.min.mjs',
                    import.meta.url
                ).href;
            } catch {
                // fake-worker fallback
            }
        }
    } catch {
        return null;
    }
    if (!pdfjs) return null;
    try {
        const data = await file.arrayBuffer();
        const doc = await pdfjs.getDocument({ data }).promise;
        const pageNo = Math.min(Math.max(pageIndex0, 0), doc.numPages - 1);
        const page = await doc.getPage(pageNo + 1);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        await page.render({ canvasContext: ctx, viewport } as never).promise;
        return {
            dataUrl: canvas.toDataURL('image/png'),
            widthMeters: viewport.width / 2 * PT_TO_M,
            heightMeters: viewport.height / 2 * PT_TO_M,
            pageCount: doc.numPages,
        };
    } catch {
        return null;
    }
}

/** Page count probe without rendering (PDFATTACH page prompt range). */
export async function pdfPageCount(file: File): Promise<number | null> {
    const raster = await renderPdfPage(file, 0);
    return raster?.pageCount ?? null;
}
